import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { propertiesAPI, filterPresetsAPI, isSessionExpired, SESSION_EXPIRED_SENTINEL } from '../services/api'
import { Filter, ActiveFilters, UserTier, SavedFilterPreset } from '../types/filters'
import { Deal } from '../types/deal'
import { filterRegistry } from '../utils/filterRegistry'
import { FilterEngine } from '../utils/filterEngine'
import { initializeFilters } from '../config/filters'

// Cache expiry constants
const CACHE_EXPIRY_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Handle thunk catch blocks: detect session expiry and sanitize error messages.
 * Session expiry returns the sentinel — reducers skip it.
 * Other errors get sanitized to never expose raw API Gateway strings.
 */
function handleThunkError(error: any, fallback: string): string {
  if (isSessionExpired(error)) return SESSION_EXPIRED_SENTINEL
  const raw = error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback
  // Sanitize: never expose raw API Gateway / authorizer messages
  const lower = (typeof raw === 'string' ? raw : '').toLowerCase()
  if (lower.includes('not authorized') || lower.includes('forbidden') || lower.includes('unauthorized') || lower.includes('deny')) {
    return 'Your session has expired. Please log in again.'
  }
  return raw || fallback
}
const ENRICHMENT_CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours

interface CrimeIncident {
  id: string
  category: string
  location: {
    latitude: string
    longitude: string
    street: { name: string }
  }
  month: string
}

interface PointOfInterest {
  id: number | string
  lat: number
  lon: number
  tags: {
    name?: string
    amenity?: string
    railway?: string
    shop?: string
    leisure?: string
    highway?: string
    [key: string]: string | undefined
  }
  type: 'school' | 'station' | 'bus_stop' | 'poi'
}

// POI location from external data table
interface POILocation {
  name: string
  lat: number
  lng: number
  address?: string
  city?: string
  postcode?: string
}

// Amenity data with locations
interface AmenityData {
  count: number
  items: POILocation[]
}

// All amenity types that can be toggled on map
interface AmenitiesDetailed {
  cafes: AmenityData
  pubs: AmenityData
  restaurants: AmenityData
  bars: AmenityData
  supermarkets: AmenityData
  pharmacies: AmenityData
  libraries: AmenityData
  hospitals: AmenityData
  parks: AmenityData
}

// Enrichment cache by postcode (not property ID)
interface PostcodeEnrichmentCache {
  fetchedAt: number
  geo?: {
    postcode: string
    postcodeCompact: string
    sector: string
    outcode: string
    lsoa: string
    lsoaName?: string
    localAuthority: string
    localAuthorityName?: string
    latitude: number
    longitude: number
  }
  crime?: {
    total_crimes_12m: number
    safety_rating: number
    crime_trend: string
    crime_breakdown?: Record<string, number>
    lsoa: string
    data_month: string
  }
  stations?: AmenityData
  schools?: {
    primary: { count_1km: number; outstanding_count: number; items?: POILocation[] }
    secondary: { count_2km: number; outstanding_count: number; items?: POILocation[] }
  }
  cafes?: AmenityData
  pubs?: AmenityData
  restaurants?: AmenityData
  bars?: AmenityData
  supermarkets?: AmenityData
  pharmacies?: AmenityData
  libraries?: AmenityData
  hospitals?: AmenityData
  parks?: AmenityData
  bus_stops?: AmenityData
}

interface PropertyEnrichmentCache {
  crimeData?: {
    incidents: CrimeIncident[]
    fetchedAt: number
  }
  poiData?: {
    pois: PointOfInterest[]
    fetchedAt: number
  }
}

interface DealsState {
  deals: Deal[]
  unlockedDeals: string[]
  favourites: string[]
  recommendedDeals: string[]
  hasFetchedRecommended: boolean // Track if we've attempted to fetch (even if empty)
  hasFetchedUnlocked: boolean // Track if we've attempted to fetch unlocked deals
  hasFetchedFavourites: boolean // Track if we've attempted to fetch favourites
  hasFetchedFilterPresets: boolean // Track if we've attempted to fetch filter presets
  hasFetchedPreferences: boolean // Track if we've attempted to fetch user preferences
  // New filter system
  activeFilters: ActiveFilters // Current filter values
  filterDefinitions: Filter[] // Available filter definitions
  savedFilterPresets: SavedFilterPreset[] // User-saved filter presets
  loading: boolean
  error: string | null
  lastFetched: number | null
  cacheExpiry: number // in milliseconds
  enrichmentCache: Record<string, PropertyEnrichmentCache> // propertyId -> cache (legacy)
  postcodeEnrichmentCache: Record<string, PostcodeEnrichmentCache> // postcode -> cache (new)
  enrichmentCacheExpiry: number // 24 hours in milliseconds
  totalDeals: number // Total deals across all properties (one property can have multiple strategy deals)
  // Server-side search state
  searchDeals: Deal[] // Deals from server-side search (paginated)
  searchTotalProperties: number
  searchTotalDeals: number
  searchNextCursor: string | null
  searchHasMore: boolean
  searchLoading: boolean
  // Per-property loading states
  unlockingPropertyIds: string[] // IDs of properties being unlocked
  favouriteTogglingPropertyIds: string[] // IDs of properties with favourite toggle in progress
}

const initialState: DealsState = {
  deals: [],
  unlockedDeals: [],
  favourites: [],
  recommendedDeals: [],
  hasFetchedRecommended: false,
  hasFetchedUnlocked: false,
  hasFetchedFavourites: false,
  hasFetchedFilterPresets: false,
  hasFetchedPreferences: false,
  // New filter system
  activeFilters: {},
  filterDefinitions: [],
  savedFilterPresets: [],
  loading: false,
  error: null,
  lastFetched: null,
  cacheExpiry: CACHE_EXPIRY_MS,
  enrichmentCache: {},
  postcodeEnrichmentCache: {},
  enrichmentCacheExpiry: ENRICHMENT_CACHE_EXPIRY_MS,
  totalDeals: 0,
  searchDeals: [],
  searchTotalProperties: 0,
  searchTotalDeals: 0,
  searchNextCursor: null,
  searchHasMore: false,
  searchLoading: false,
  unlockingPropertyIds: [],
  favouriteTogglingPropertyIds: []
}

/**
 * Convert Redux activeFilters + filter definitions to API query params for /properties/search.
 * Only includes non-default filter values.
 */
export function filtersToSearchParams(
  activeFilters: ActiveFilters,
  filterDefinitions: Filter[],
  sort: string,
  cursor?: string | null,
): Record<string, string> {
  const params: Record<string, string> = {}

  for (const [filterId, value] of Object.entries(activeFilters)) {
    const def = filterDefinitions.find(f => f.id === filterId)
    if (!def || !def.enabled) continue

    // Skip default values
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      if (Array.isArray(def.defaultValue) && value.length === 2 &&
          def.type === 'range' &&
          value[0] === (def.defaultValue as number[])[0] &&
          value[1] === (def.defaultValue as number[])[1]) continue
    }

    switch (filterId) {
      case 'ai_strategy': {
        const selected = value as string[]
        if (selected.length > 0) params.strategy = selected.join(',')
        break
      }
      case 'gross_yield': {
        const [min, max] = value as [number, number]
        if (min !== (def.defaultValue as number[])[0]) params.minYield = String(min)
        if (max !== (def.defaultValue as number[])[1]) params.maxYield = String(max)
        break
      }
      case 'estimated_rent': {
        const [min, max] = value as [number, number]
        if (min !== (def.defaultValue as number[])[0]) params.minRent = String(min)
        if (max !== (def.defaultValue as number[])[1]) params.maxRent = String(max)
        break
      }
      case 'purchase_price': {
        const [min, max] = value as [number, number]
        if (min !== (def.defaultValue as number[])[0]) params.minPrice = String(min)
        if (max !== (def.defaultValue as number[])[1]) params.maxPrice = String(max)
        break
      }
      case 'bedrooms': {
        const selected = value as string[]
        if (selected.length > 0) params.bedrooms = selected.join(',')
        break
      }
      case 'property_type': {
        const selected = value as string[]
        if (selected.length > 0) params.propertyType = selected.join(',')
        break
      }
      case 'total_score': {
        const [min, max] = value as [number, number]
        if (min !== (def.defaultValue as number[])[0]) params.minScore = String(min)
        if (max !== (def.defaultValue as number[])[1]) params.maxScore = String(max)
        break
      }
      case 'quality_rating': {
        const selected = value as string[]
        if (selected.length > 0) params.qualityRating = selected.join(',')
        break
      }
      case 'crime_safety': {
        const [min, max] = value as [number, number]
        if (min !== (def.defaultValue as number[])[0]) params.minCrime = String(min)
        if (max !== (def.defaultValue as number[])[1]) params.maxCrime = String(max)
        break
      }
      case 'location_search': {
        const raw = (value as string ?? '').trim()
        if (raw.length > 0) params.location = raw
        break
      }
    }
  }

  params.sort = sort
  params.pageSize = '100'
  if (cursor) params.cursor = cursor

  return params
}

// Async thunks

/**
 * Server-side search: fetches one page of deals from /properties/search.
 * Filters, sorting, and counts are all computed server-side.
 */
export const searchDeals = createAsyncThunk(
  'deals/searchDeals',
  async (
    { sort, cursor, append }: { sort: string; cursor?: string | null; append?: boolean },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as { deals: DealsState }
      const params = filtersToSearchParams(
        state.deals.activeFilters,
        state.deals.filterDefinitions,
        sort,
        cursor,
      )
      const response = await propertiesAPI.search(params)
      return {
        deals: response.deals || [],
        totalProperties: response.totalProperties || 0,
        totalDeals: response.totalDeals || 0,
        nextCursor: response.nextCursor || null,
        hasMore: response.hasMore || false,
        append: append || false,
      }
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to search deals'))
    }
  }
)

// Legacy thunk — used by MyDeals and other pages that still load all properties
export const fetchDeals = createAsyncThunk(
  'deals/fetchDeals',
  async (filters: any = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { deals: DealsState }
      const now = Date.now()

      // Check if cache is still valid
      if (
        state.deals.lastFetched &&
        now - state.deals.lastFetched < state.deals.cacheExpiry
      ) {
        // Return cached data
        return { deals: state.deals.deals, fromCache: true }
      }

      // Fetch fresh data
      const response = await propertiesAPI.list(filters)
      return { deals: response.deals, totalDeals: response.totalDeals || 0, fromCache: false }
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch deals'))
    }
  }
)

export const fetchDeal = createAsyncThunk(
  'deals/fetchDeal',
  async (dealId: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { deals: DealsState }

      // Check if deal already exists in cache with full details
      // Look up by id (internalId) first, fallback to propertyId for backwards compatibility
      const cachedDeal = state.deals.deals.find(d => d.id === dealId || d.propertyId === dealId)

      // IMPORTANT: Only use cache if deal has hasFullDetails flag set to true
      // This flag is ONLY set when we fetch via getProperty endpoint, NOT list endpoint
      if (cachedDeal && (cachedDeal as any).hasFullDetails === true) {
        console.log('[fetchDeal] Using cached data for', dealId)
        return { ...cachedDeal, fromCache: true }
      }

      // Otherwise fetch fresh data from API (dealId is now the internalId)
      console.log('[fetchDeal] Fetching fresh data for', dealId)
      const response = await propertiesAPI.get(dealId)
      return { ...response, fromCache: false }
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch deal'))
    }
  }
)

export const fetchUnlockedDeals = createAsyncThunk(
  'deals/fetchUnlockedDeals',
  async (forceRefresh: boolean | void = false, { getState, rejectWithValue }) => {
    const shouldRefresh = forceRefresh === true
    try {
      const state = getState() as { deals: DealsState }

      // Return cached data if available (even if empty, use hasFetchedUnlocked flag)
      if (!shouldRefresh && state.deals.hasFetchedUnlocked) {
        // Return full deal objects, not just IDs
        const unlockedDeals = state.deals.deals.filter(d =>
          state.deals.unlockedDeals.includes(d.propertyId)
        )
        return { unlocks: unlockedDeals, fromCache: true }
      }

      const response = await propertiesAPI.getUnlocked()
      return { unlocks: response.unlocks || [], fromCache: false }
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch unlocked deals'))
    }
  }
)

export const fetchFavourites = createAsyncThunk(
  'deals/fetchFavourites',
  async (forceRefresh: boolean | void = false, { getState, rejectWithValue }) => {
    const shouldRefresh = forceRefresh === true
    try {
      const state = getState() as { deals: DealsState }

      // Return cached data if available (even if empty, use hasFetchedFavourites flag)
      if (!shouldRefresh && state.deals.hasFetchedFavourites) {
        // Return full deal objects, not just IDs
        const favouriteDeals = state.deals.deals.filter(d =>
          state.deals.favourites.includes(d.propertyId)
        )
        return { favourites: favouriteDeals, fromCache: true }
      }

      const response = await propertiesAPI.getFavourites()
      return { favourites: response.favourites || [], fromCache: false }
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch favourites'))
    }
  }
)

export const fetchRecommended = createAsyncThunk(
  'deals/fetchRecommended',
  async (forceRefresh: boolean | void = false, { getState, rejectWithValue }) => {
    const shouldRefresh = forceRefresh === true
    try {
      const state = getState() as { deals: DealsState }

      // Return cached data if available (even if empty, use hasFetchedRecommended flag)
      if (!shouldRefresh && state.deals.hasFetchedRecommended) {
        // Return full deal objects, not just IDs
        const recommendedDeals = state.deals.deals.filter(d =>
          state.deals.recommendedDeals.includes(d.propertyId)
        )
        return { recommended: recommendedDeals, fromCache: true }
      }

      const response = await propertiesAPI.getRecommended()
      return { recommended: response.recommended || [], fromCache: false }
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch recommended deals'))
    }
  }
)

export const unlockDeal = createAsyncThunk(
  'deals/unlockDeal',
  async (dealId: string, { getState, rejectWithValue }) => {
    try {
      // dealId is now the internalId (UUID)
      const response = await propertiesAPI.unlock(dealId)

      // Find the deal to get its propertyId for internal state tracking
      const state = getState() as { deals: DealsState }
      const deal = state.deals.deals.find(d => d.id === dealId || d.propertyId === dealId)
        || state.deals.searchDeals.find(d => d.id === dealId || d.propertyId === dealId)
      const propertyId = deal?.propertyId || dealId  // Fallback to dealId if not found

      const result = {
        dealId,  // The internalId used for API calls
        propertyId,  // The propertyId for internal state tracking
        enrichment: response.enrichment,
        analyses: response.analyses,  // NEW: Multi-strategy analyses
        strategies: response.strategies,  // NEW: Available strategies
        primaryStrategy: response.primaryStrategy,  // NEW: Primary strategy
        creditsRemaining: response.creditsRemaining
      }
      return result
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to unlock deal'))
    }
  }
)

export const toggleFavourite = createAsyncThunk(
  'deals/toggleFavourite',
  async (dealId: string, { getState, rejectWithValue }) => {
    try {
      // dealId is now the internalId (UUID)
      const response = await propertiesAPI.toggleFavourite(dealId)

      // Use server-returned propertyId (backend resolves internalId → propertyId)
      // Fallback: search both deal arrays for the propertyId
      let propertyId = response.propertyId
      if (!propertyId) {
        const state = getState() as { deals: DealsState }
        const deal = state.deals.deals.find(d => d.id === dealId || d.propertyId === dealId)
          || state.deals.searchDeals.find(d => d.id === dealId || d.propertyId === dealId)
        propertyId = deal?.propertyId || dealId
      }

      return { dealId, propertyId, isFavourited: response.isFavourited }
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to toggle favourite'))
    }
  }
)

// ===== FILTER PRESET THUNKS =====
export const fetchFilterPresets = createAsyncThunk(
  'deals/fetchFilterPresets',
  async (forceRefresh: boolean | void = false, { getState, rejectWithValue }) => {
    const shouldRefresh = forceRefresh === true
    try {
      const state = getState() as { deals: DealsState }

      // Return cached data if available (even if empty, use hasFetchedFilterPresets flag)
      if (!shouldRefresh && state.deals.hasFetchedFilterPresets) {
        return { presets: state.deals.savedFilterPresets, fromCache: true }
      }

      const response = await filterPresetsAPI.getAll()
      return { presets: response.presets || [], fromCache: false }
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch filter presets'))
    }
  }
)

export const saveFilterPresetThunk = createAsyncThunk(
  'deals/saveFilterPreset',
  async (preset: { name: string; description?: string; filters: ActiveFilters }, { rejectWithValue }) => {
    try {
      const response = await filterPresetsAPI.save(preset)
      return response.preset
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to save filter preset'))
    }
  }
)

export const updateFilterPresetThunk = createAsyncThunk(
  'deals/updateFilterPreset',
  async (update: { id: string; name?: string; description?: string; filters?: ActiveFilters }, { rejectWithValue }) => {
    try {
      const response = await filterPresetsAPI.update(update.id, {
        name: update.name,
        description: update.description,
        filters: update.filters
      })
      return response.preset
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to update filter preset'))
    }
  }
)

export const deleteFilterPresetThunk = createAsyncThunk(
  'deals/deleteFilterPreset',
  async (id: string, { rejectWithValue }) => {
    try {
      await filterPresetsAPI.delete(id)
      return id
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to delete filter preset'))
    }
  }
)

// ===== USER PREFERENCES THUNKS =====
export const fetchPreferences = createAsyncThunk(
  'deals/fetchPreferences',
  async (forceRefresh: boolean | void = false, { getState, rejectWithValue }) => {
    const shouldRefresh = forceRefresh === true
    try {
      const state = getState() as { deals: DealsState }

      // Return cached data if available (even if empty, use hasFetchedPreferences flag)
      if (!shouldRefresh && state.deals.hasFetchedPreferences) {
        return { preferences: { activeFilters: state.deals.activeFilters }, fromCache: true }
      }

      const response = await propertiesAPI.getPreferences()
      return { preferences: response.preferences || null, fromCache: false }
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to fetch preferences'))
    }
  }
)

export const updatePreferences = createAsyncThunk(
  'deals/updatePreferences',
  async (preferences: { activeFilters: ActiveFilters }, { rejectWithValue }) => {
    try {
      await propertiesAPI.updatePreferences(preferences)
      return preferences
    } catch (error: any) {
      return rejectWithValue(handleThunkError(error, 'Failed to update preferences'))
    }
  }
)

/** Set state.error only if the payload is not a session-expiry sentinel */
function setErrorIfNotExpired(state: DealsState, payload: unknown, fallback: string) {
  const msg = (payload as string) || fallback
  if (msg !== SESSION_EXPIRED_SENTINEL) {
    state.error = msg
  }
}

const dealsSlice = createSlice({
  name: 'deals',
  initialState,
  reducers: {
    // ===== NEW FILTER SYSTEM ACTIONS =====
    /**
     * Initialize filter definitions from registry
     */
    initializeFilterDefinitions: (state) => {
      // Initialize the filter registry first
      initializeFilters()

      // Then get the enabled filters
      state.filterDefinitions = filterRegistry.getEnabled()

      // Initialize active filters with default values
      state.activeFilters = state.filterDefinitions.reduce((acc, filter) => {
        acc[filter.id] = filter.defaultValue
        return acc
      }, {} as ActiveFilters)
    },

    /**
     * Set a single filter value
     */
    setFilterValue: (state, action: PayloadAction<{ filterId: string; value: any }>) => {
      const { filterId, value } = action.payload
      state.activeFilters[filterId] = value

      // Persist to localStorage
      try {
        localStorage.setItem('dealigent_active_filters', JSON.stringify(state.activeFilters))
      } catch (error) {
        // Silent fail
      }
    },

    /**
     * Set multiple filter values at once
     */
    setMultipleFilters: (state, action: PayloadAction<ActiveFilters>) => {
      state.activeFilters = { ...state.activeFilters, ...action.payload }

      // Persist to localStorage
      try {
        localStorage.setItem('dealigent_active_filters', JSON.stringify(state.activeFilters))
      } catch (error) {
        // Silent fail
      }
    },

    /**
     * Clear a single filter (reset to default)
     */
    clearFilter: (state, action: PayloadAction<string>) => {
      const filterId = action.payload
      const filter = state.filterDefinitions.find(f => f.id === filterId)
      if (filter) {
        state.activeFilters[filterId] = filter.defaultValue
      }

      // Persist to localStorage
      try {
        localStorage.setItem('dealigent_active_filters', JSON.stringify(state.activeFilters))
      } catch (error) {
        // Silent fail
      }
    },

    /**
     * Clear all filters (reset all to defaults)
     */
    clearAllFilters: (state) => {
      state.activeFilters = state.filterDefinitions.reduce((acc, filter) => {
        acc[filter.id] = filter.defaultValue
        return acc
      }, {} as ActiveFilters)

      // Persist to localStorage
      try {
        localStorage.setItem('dealigent_active_filters', JSON.stringify(state.activeFilters))
      } catch (error) {
        // Silent fail
      }
    },

    /**
     * Load filters from localStorage
     */
    loadFiltersFromStorage: (state) => {
      try {
        const stored = localStorage.getItem('dealigent_active_filters')
        if (stored) {
          const parsedFilters = JSON.parse(stored)
          state.activeFilters = { ...state.activeFilters, ...parsedFilters }
        }
      } catch (error) {
        // Silent fail
      }
    },

    // ===== SAVED FILTER PRESETS =====
    /**
     * Load a saved preset (apply its filters) - synchronous action
     */
    loadFilterPreset: (state, action: PayloadAction<string>) => {
      const presetId = action.payload
      const preset = state.savedFilterPresets.find(p => p.id === presetId)
      if (preset) {
        state.activeFilters = { ...preset.filters }

        // Persist active filters to localStorage
        try {
          localStorage.setItem('dealigent_active_filters', JSON.stringify(state.activeFilters))
        } catch (error) {
          // Silent fail
        }
      }
    },

    // ===== OTHER ACTIONS =====
    clearCache: (state) => {
      state.lastFetched = null
    },
    updateDeal: (state, action: PayloadAction<Deal>) => {
      const index = state.deals.findIndex(d => d.propertyId === action.payload.propertyId)
      if (index !== -1) {
        state.deals[index] = action.payload
      }
    },
    setCrimeData: (state, action: PayloadAction<{ propertyId: string; incidents: CrimeIncident[] }>) => {
      if (!state.enrichmentCache[action.payload.propertyId]) {
        state.enrichmentCache[action.payload.propertyId] = {}
      }
      state.enrichmentCache[action.payload.propertyId].crimeData = {
        incidents: action.payload.incidents,
        fetchedAt: Date.now()
      }
    },
    setPOIData: (state, action: PayloadAction<{ propertyId: string; pois: PointOfInterest[] }>) => {
      if (!state.enrichmentCache[action.payload.propertyId]) {
        state.enrichmentCache[action.payload.propertyId] = {}
      }
      state.enrichmentCache[action.payload.propertyId].poiData = {
        pois: action.payload.pois,
        fetchedAt: Date.now()
      }
    },
    clearEnrichmentCache: (state) => {
      state.enrichmentCache = {}
      state.postcodeEnrichmentCache = {}
    },

    // New postcode-based enrichment cache actions
    setPostcodeEnrichment: (state, action: PayloadAction<{
      postcode: string
      data: Partial<PostcodeEnrichmentCache>
    }>) => {
      const { postcode, data } = action.payload
      const normalizedPostcode = postcode.replace(/\s+/g, '').toUpperCase()

      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }

      state.postcodeEnrichmentCache[normalizedPostcode] = {
        ...state.postcodeEnrichmentCache[normalizedPostcode],
        ...data,
        fetchedAt: Date.now()
      }
    },

    setPostcodeStations: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].stations = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodeCafes: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].cafes = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodePubs: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].pubs = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodeRestaurants: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].restaurants = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodeBars: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].bars = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodeSupermarkets: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].supermarkets = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodePharmacies: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].pharmacies = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodeLibraries: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].libraries = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodeHospitals: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].hospitals = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodeParks: (state, action: PayloadAction<{ postcode: string; data: AmenityData }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].parks = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodeCrime: (state, action: PayloadAction<{ postcode: string; data: PostcodeEnrichmentCache['crime'] }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].crime = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    setPostcodeSchools: (state, action: PayloadAction<{ postcode: string; data: PostcodeEnrichmentCache['schools'] }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      state.postcodeEnrichmentCache[normalizedPostcode].schools = action.payload.data
      state.postcodeEnrichmentCache[normalizedPostcode].fetchedAt = Date.now()
    },

    // Set all amenities at once from batch API call
    setAllAmenities: (state, action: PayloadAction<{
      postcode: string;
      data: {
        cafes?: AmenityData;
        pubs?: AmenityData;
        restaurants?: AmenityData;
        bars?: AmenityData;
        supermarkets?: AmenityData;
        pharmacies?: AmenityData;
        libraries?: AmenityData;
        hospitals?: AmenityData;
        parks?: AmenityData;
        stations?: AmenityData;
        bus_stops?: AmenityData;
        schools?: AmenityData;
      }
    }>) => {
      const normalizedPostcode = action.payload.postcode.replace(/\s+/g, '').toUpperCase()
      if (!state.postcodeEnrichmentCache[normalizedPostcode]) {
        state.postcodeEnrichmentCache[normalizedPostcode] = { fetchedAt: Date.now() }
      }
      const cache = state.postcodeEnrichmentCache[normalizedPostcode]
      const data = action.payload.data
      if (data.cafes) cache.cafes = data.cafes
      if (data.pubs) cache.pubs = data.pubs
      if (data.restaurants) cache.restaurants = data.restaurants
      if (data.bars) cache.bars = data.bars
      if (data.supermarkets) cache.supermarkets = data.supermarkets
      if (data.pharmacies) cache.pharmacies = data.pharmacies
      if (data.libraries) cache.libraries = data.libraries
      if (data.hospitals) cache.hospitals = data.hospitals
      if (data.parks) cache.parks = data.parks
      if (data.stations) cache.stations = data.stations
      if (data.bus_stops) cache.bus_stops = data.bus_stops
      if (data.schools) {
        // Schools from batch API has different structure - convert to cache format
        cache.schools = {
          primary: { count_1km: data.schools.count || 0, outstanding_count: 0, items: data.schools.items },
          secondary: { count_2km: 0, outstanding_count: 0, items: [] }
        }
      }
      cache.fetchedAt = Date.now()
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch deals
      .addCase(fetchDeals.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDeals.fulfilled, (state, action) => {
        state.loading = false
        if (!action.payload.fromCache) {
          // Preserve unlock status, favourite status, and other client-side state when refreshing deals
          const newDeals = action.payload.deals.map((newDeal: any) => {
            const existingDeal = state.deals.find(d => d.propertyId === newDeal.propertyId) as any
            const isInUnlockedArray = state.unlockedDeals.includes(newDeal.propertyId)
            const isInFavouritesArray = state.favourites.includes(newDeal.propertyId)

            // Determine unlock status - preserve if already unlocked
            const wasUnlocked = existingDeal?.isUnlocked || isInUnlockedArray

            return {
              ...newDeal,
              // Preserve unlock status
              isUnlocked: wasUnlocked,
              isLocked: wasUnlocked ? false : newDeal.isLocked,
              // Preserve favourite status
              isFavourite: existingDeal?.isFavourite || isInFavouritesArray,
              // Preserve full details if already fetched
              hasFullDetails: existingDeal?.hasFullDetails || false,
              fullDetails: existingDeal?.fullDetails,
              enrichment: existingDeal?.enrichment || newDeal.enrichment
            }
          })
          state.deals = newDeals
          state.totalDeals = action.payload.totalDeals || 0
          state.lastFetched = Date.now()
        }
      })
      .addCase(fetchDeals.rejected, (state, action) => {
        state.loading = false
        const msg = action.error.message || 'Failed to fetch deals'
        if (msg !== SESSION_EXPIRED_SENTINEL) state.error = msg
      })

      // Server-side search
      .addCase(searchDeals.pending, (state) => {
        state.searchLoading = true
        state.error = null
      })
      .addCase(searchDeals.fulfilled, (state, action) => {
        state.searchLoading = false
        const { deals, totalProperties, totalDeals, nextCursor, hasMore, append } = action.payload
        if (append) {
          // Append to existing results (infinite scroll)
          state.searchDeals = [...state.searchDeals, ...deals]
        } else {
          // Replace results (new search / filter change)
          state.searchDeals = deals
        }
        state.searchTotalProperties = totalProperties
        state.searchTotalDeals = totalDeals
        state.searchNextCursor = nextCursor
        state.searchHasMore = hasMore
      })
      .addCase(searchDeals.rejected, (state, action) => {
        state.searchLoading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to search deals')
      })

      // Fetch unlocked deals
      .addCase(fetchUnlockedDeals.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUnlockedDeals.fulfilled, (state, action) => {
        state.loading = false
        // Handle both old format (direct array) and new format (with fromCache)
        const unlocks = (action.payload as any).unlocks || action.payload

        state.unlockedDeals = unlocks.map((u: any) => u.propertyId)
        state.hasFetchedUnlocked = true // Mark as fetched

        // Replace deals array with unlocked deals (they have complete data)
        unlocks.forEach((unlock: any) => {
          const dealIndex = state.deals.findIndex(d => d.propertyId === unlock.propertyId)
          if (dealIndex !== -1) {
            // Replace entire deal with fresh data from backend
            // IMPORTANT: Preserve unlockCount and favouriteCount from list endpoint
            const existingDeal = state.deals[dealIndex]
            state.deals[dealIndex] = {
              ...unlock,
              isUnlocked: true,
              isFavourite: existingDeal.isFavourite, // Preserve favourite status
              unlockCount: existingDeal.unlockCount, // Preserve trending counts
              favouriteCount: existingDeal.favouriteCount,
              // Preserve full data if previously fetched via detail page
              analyses: existingDeal.analyses || unlock.analyses,
              enrichment: existingDeal.enrichment || unlock.enrichment,
              hasFullDetails: (existingDeal as any).hasFullDetails || false
            }
          } else {
            // Add new deal to the array
            state.deals.push({
              ...unlock,
              isUnlocked: true
            })
          }
        })
      })
      .addCase(fetchUnlockedDeals.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to fetch unlocked deals')
      })

      // Fetch favourites
      .addCase(fetchFavourites.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFavourites.fulfilled, (state, action) => {
        state.loading = false
        // Handle both old format (direct array) and new format (with fromCache)
        const favourites = (action.payload as any).favourites || action.payload

        state.favourites = favourites.map((f: any) => f.propertyId)
        state.hasFetchedFavourites = true // Mark as fetched

        // Add favourite deals to the array if they don't exist
        favourites.forEach((favourite: any) => {
          const dealIndex = state.deals.findIndex(d => d.propertyId === favourite.propertyId)
          if (dealIndex === -1) {
            // Add new deal to the array
            state.deals.push({
              ...favourite,
              isFavourite: true
            })
          }
        })
      })
      .addCase(fetchFavourites.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to fetch favourites')
      })

      // Fetch single deal (from detail page)
      .addCase(fetchDeal.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDeal.fulfilled, (state, action) => {
        state.loading = false
        const fetchedDeal = action.payload as any
        // Find by id (internalId) first, fallback to propertyId for backwards compatibility
        const dealIndex = state.deals.findIndex(d =>
          d.id === fetchedDeal.id || d.propertyId === fetchedDeal.propertyId
        )

        // If data is from cache, don't update (it's already in the array)
        if (fetchedDeal.fromCache) {
          console.log('[fetchDeal.fulfilled] Using cached data, no update needed')
          return
        }

        // Check if this property is in the unlockedDeals array (source of truth for unlock status)
        const isInUnlockedArray = state.unlockedDeals.includes(fetchedDeal.propertyId)

        if (dealIndex !== -1) {
          // Update existing deal in the array with full details and enrichment
          // IMPORTANT: Spread fetchedDeal FIRST, then override with existing deal to preserve fields
          const existingDeal = state.deals[dealIndex]

          // Determine unlock status - preserve if already unlocked in Redux or in unlockedDeals array
          const wasUnlocked = existingDeal.isUnlocked || isInUnlockedArray

          state.deals[dealIndex] = {
            ...fetchedDeal,              // New data from API (description, address, etc.)
            ...state.deals[dealIndex],   // Preserve existing fields (unlockCount, favouriteCount, etc.)
            // Explicitly set fields that should come from fetchedDeal
            fullDetails: fetchedDeal.fullDetails || existingDeal.fullDetails,
            enrichment: fetchedDeal.enrichment || existingDeal.enrichment,
            images: fetchedDeal.images || existingDeal.images,
            description: fetchedDeal.description,
            address: fetchedDeal.address,
            // Agent personal details removed — only listing URL exposed
            keyFeatures: fetchedDeal.keyFeatures,
            floorplanUrls: fetchedDeal.floorplanUrls,
            virtualTourUrl: fetchedDeal.virtualTourUrl,
            epcRating: fetchedDeal.epcRating,
            // Detail-owned fields must win over cached list/search entries.
            // Search results intentionally strip exact location fields.
            teaser: fetchedDeal.teaser || existingDeal.teaser,
            latitude: fetchedDeal.latitude ?? existingDeal.latitude,
            longitude: fetchedDeal.longitude ?? existingDeal.longitude,
            source: fetchedDeal.source || existingDeal.source,
            analyses: fetchedDeal.analyses || existingDeal.analyses,
            strategies: fetchedDeal.strategies || existingDeal.strategies,
            primaryStrategy: fetchedDeal.primaryStrategy || existingDeal.primaryStrategy,
            detailPolicy: fetchedDeal.detailPolicy || existingDeal.detailPolicy,
            // CRITICAL: Explicitly preserve unlock status to prevent race conditions
            isUnlocked: wasUnlocked,
            isLocked: wasUnlocked ? false : (existingDeal.isLocked ?? fetchedDeal.isLocked),
            // Mark as having full details (fetched via getProperty endpoint)
            hasFullDetails: true
          }
        } else {
          // Add new deal to the array
          state.deals.push({
            ...fetchedDeal,
            // Check unlockedDeals array for unlock status
            isUnlocked: isInUnlockedArray || fetchedDeal.isUnlocked,
            isLocked: isInUnlockedArray ? false : fetchedDeal.isLocked,
            // Mark as having full details
            hasFullDetails: true
          })
        }
      })
      .addCase(fetchDeal.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to fetch deal')
      })

      // Fetch recommended deals
      .addCase(fetchRecommended.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRecommended.fulfilled, (state, action) => {
        state.loading = false
        // Handle both old format (direct array) and new format (with fromCache)
        const recommended = (action.payload as any).recommended || action.payload

        state.recommendedDeals = recommended.map((r: any) => r.propertyId)
        state.hasFetchedRecommended = true // Mark as fetched even if empty
        // Add recommended deals to the array if they don't exist
        recommended.forEach((recommendedDeal: any) => {
          const dealIndex = state.deals.findIndex(d => d.propertyId === recommendedDeal.propertyId)
          if (dealIndex === -1) {
            // Add new deal to the array
            state.deals.push(recommendedDeal)
          }
        })
      })
      .addCase(fetchRecommended.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to fetch recommended deals')
      })

      // Unlock deal
      .addCase(unlockDeal.pending, (state, action) => {
        // Add deal ID to unlocking array (now using internalId)
        const dealId = action.meta.arg
        if (!state.unlockingPropertyIds.includes(dealId)) {
          state.unlockingPropertyIds.push(dealId)
        }
        state.error = null
      })
      .addCase(unlockDeal.fulfilled, (state, action) => {
        // Remove deal ID from unlocking array
        const { dealId, propertyId, enrichment, analyses, strategies, primaryStrategy } = action.payload
        state.unlockingPropertyIds = state.unlockingPropertyIds.filter(id => id !== dealId)
        // Find deal in both arrays (browse page uses searchDeals, other pages use deals)
        const deal = state.deals.find(d => d.id === dealId || d.propertyId === propertyId)
          || state.searchDeals.find(d => d.id === dealId || d.propertyId === propertyId)
        if (deal) {
          deal.isUnlocked = true
          const dealAny = deal as any
          dealAny.isLocked = false  // Also set isLocked to false for UI rendering
          // Clear hasFullDetails to force re-fetch with complete unlocked data
          dealAny.hasFullDetails = false

          // NEW: Multi-strategy analyses
          if (analyses) {
            (deal as any).analyses = analyses
          }

          // Backend returns enrichment data (ENRICHMENT record)
          if (enrichment) {
            deal.enrichment = enrichment

            // Also update teaser with enrichment-derived fields (strategy, yield, rent)
            // This ensures deal cards show complete information after unlock
            if (enrichment.aiAnalysis?.strategy) {
              deal.teaser.strategy = enrichment.aiAnalysis.strategy
            }
            if (enrichment.btl_analysis?.gross_yield) {
              deal.teaser.estimatedYield = `${enrichment.btl_analysis.gross_yield.toFixed(1)}%`
            }
            if (enrichment.btl_analysis?.monthly_rent) {
              deal.teaser.estimatedRent = enrichment.btl_analysis.monthly_rent
            } else if (enrichment.ons_rental?.monthly_rent) {
              deal.teaser.estimatedRent = enrichment.ons_rental.monthly_rent
            }
            if (enrichment.scoring?.total_score !== undefined) {
              deal.teaser.totalScore = enrichment.scoring.total_score
            }
            if (enrichment.scoring?.quality_rating) {
              deal.teaser.qualityRating = enrichment.scoring.quality_rating
            }
          }

          // NEW: Update teaser with multi-strategy info
          if (strategies) {
            deal.teaser.strategies = strategies
          }
          if (primaryStrategy) {
            deal.teaser.primaryStrategy = primaryStrategy
          }
        }
        if (!state.unlockedDeals.includes(propertyId)) {
          state.unlockedDeals.push(propertyId)
        }
      })
      .addCase(unlockDeal.rejected, (state, action) => {
        // Remove deal ID from unlocking array
        const dealId = action.meta.arg
        state.unlockingPropertyIds = state.unlockingPropertyIds.filter(id => id !== dealId)
        setErrorIfNotExpired(state, action.payload, 'Failed to unlock deal')
      })

      // Toggle favourite
      .addCase(toggleFavourite.pending, (state, action) => {
        // Add deal ID to favourite toggling array (now using internalId)
        const dealId = action.meta.arg
        if (!state.favouriteTogglingPropertyIds.includes(dealId)) {
          state.favouriteTogglingPropertyIds.push(dealId)
        }
        state.error = null
      })
      .addCase(toggleFavourite.fulfilled, (state, action) => {
        // Remove deal ID from favourite toggling array
        const { dealId, propertyId, isFavourited } = action.payload
        state.favouriteTogglingPropertyIds = state.favouriteTogglingPropertyIds.filter(id => id !== dealId)

        // Update favourites ID array (using propertyId for internal state)
        if (isFavourited) {
          if (!state.favourites.includes(propertyId)) {
            state.favourites.push(propertyId)
          }
        } else {
          state.favourites = state.favourites.filter(id => id !== propertyId)
        }

        // Update isFavourite flag on deal objects in both arrays
        const deal = state.deals.find(d => d.id === dealId || d.propertyId === propertyId)
        if (deal) {
          deal.isFavourite = isFavourited
        }
        const searchDeal = state.searchDeals.find(d => d.id === dealId || d.propertyId === propertyId)
        if (searchDeal) {
          searchDeal.isFavourite = isFavourited
        }
      })
      .addCase(toggleFavourite.rejected, (state, action) => {
        // Remove deal ID from favourite toggling array
        const dealId = action.meta.arg
        state.favouriteTogglingPropertyIds = state.favouriteTogglingPropertyIds.filter(id => id !== dealId)
        setErrorIfNotExpired(state, action.payload, 'Failed to toggle favourite')
      })

      // Fetch filter presets
      .addCase(fetchFilterPresets.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFilterPresets.fulfilled, (state, action) => {
        state.loading = false
        // Handle both old format (direct array) and new format (with fromCache)
        const presets = (action.payload as any).presets || action.payload
        state.savedFilterPresets = presets
        state.hasFetchedFilterPresets = true // Mark as fetched
      })
      .addCase(fetchFilterPresets.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to fetch filter presets')
      })

      // Save filter preset
      .addCase(saveFilterPresetThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(saveFilterPresetThunk.fulfilled, (state, action) => {
        state.loading = false
        state.savedFilterPresets.push(action.payload)
      })
      .addCase(saveFilterPresetThunk.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to save filter preset')
      })

      // Update filter preset
      .addCase(updateFilterPresetThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateFilterPresetThunk.fulfilled, (state, action) => {
        state.loading = false
        const index = state.savedFilterPresets.findIndex(p => p.id === action.payload.id)
        if (index !== -1) {
          state.savedFilterPresets[index] = action.payload
        }
      })
      .addCase(updateFilterPresetThunk.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to update filter preset')
      })

      // Delete filter preset
      .addCase(deleteFilterPresetThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteFilterPresetThunk.fulfilled, (state, action) => {
        state.loading = false
        state.savedFilterPresets = state.savedFilterPresets.filter(p => p.id !== action.payload)
      })
      .addCase(deleteFilterPresetThunk.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to delete filter preset')
      })

      // Fetch user preferences
      .addCase(fetchPreferences.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.loading = false
        const preferences = (action.payload as any).preferences
        if (preferences && preferences.activeFilters) {
          state.activeFilters = preferences.activeFilters
        }
        state.hasFetchedPreferences = true // Mark as fetched
      })
      .addCase(fetchPreferences.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to fetch preferences')
      })

      // Update user preferences
      .addCase(updatePreferences.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.loading = false
        state.activeFilters = action.payload.activeFilters
      })
      .addCase(updatePreferences.rejected, (state, action) => {
        state.loading = false
        setErrorIfNotExpired(state, action.payload, 'Failed to update preferences')
      })
  }
})

export const {
  // New filter system
  initializeFilterDefinitions,
  setFilterValue,
  setMultipleFilters,
  clearFilter,
  clearAllFilters,
  loadFiltersFromStorage,
  // Saved filter presets (synchronous action for loading preset filters)
  loadFilterPreset,
  // Other actions
  clearCache,
  updateDeal,
  setCrimeData,
  setPOIData,
  clearEnrichmentCache,
  // New postcode-based enrichment cache actions
  setPostcodeEnrichment,
  setPostcodeStations,
  setPostcodeCafes,
  setPostcodePubs,
  setPostcodeRestaurants,
  setPostcodeBars,
  setPostcodeSupermarkets,
  setPostcodePharmacies,
  setPostcodeLibraries,
  setPostcodeHospitals,
  setPostcodeParks,
  setPostcodeCrime,
  setPostcodeSchools,
  // Set all amenities at once (batch API)
  setAllAmenities
} = dealsSlice.actions

export default dealsSlice.reducer

// Re-export types for backwards compatibility
export type { Deal } from '../types/deal'

// ===== SELECTORS =====

// Base selectors
const selectDeals = (state: { deals: DealsState }) => state.deals.deals
const selectActiveFilters = (state: { deals: DealsState }) => state.deals.activeFilters
const selectFilterDefinitions = (state: { deals: DealsState }) => state.deals.filterDefinitions
const selectUserTier = (state: { deals: DealsState; user: any }) => state.user?.user?.tier || 'STARTER'
const selectIsLaunchPromoForFilters = (state: { deals: DealsState; features?: { featureAccess?: { isLaunchPromo?: boolean } | null } }) => state.features?.featureAccess?.isLaunchPromo ?? false

// Selectors for per-property loading states
export const selectUnlockingPropertyIds = (state: { deals: DealsState }) => state.deals.unlockingPropertyIds
export const selectFavouriteTogglingPropertyIds = (state: { deals: DealsState }) => state.deals.favouriteTogglingPropertyIds

/**
 * Get filtered deals based on active filters (memoized)
 */
export const selectFilteredDeals = createSelector(
  [selectDeals, selectActiveFilters, selectFilterDefinitions],
  (deals, activeFilters, filterDefinitions) => {
    return FilterEngine.applyFilters(deals, activeFilters, filterDefinitions)
  }
)

/**
 * Get count of filtered deals (memoized)
 */
export const selectFilteredDealsCount = createSelector(
  [selectDeals, selectActiveFilters, selectFilterDefinitions],
  (deals, activeFilters, filterDefinitions) => {
    return FilterEngine.getFilteredCount(deals, activeFilters, filterDefinitions)
  }
)

/**
 * Check if any filters are active (memoized)
 */
export const selectHasActiveFilters = createSelector(
  [selectActiveFilters, selectFilterDefinitions],
  (activeFilters, filterDefinitions) => {
    return FilterEngine.hasActiveFilters(activeFilters, filterDefinitions)
  }
)

/**
 * Get list of active filter IDs (memoized)
 */
export const selectActiveFilterIds = createSelector(
  [selectActiveFilters, selectFilterDefinitions],
  (activeFilters, filterDefinitions) => {
    return FilterEngine.getActiveFilterIds(activeFilters, filterDefinitions)
  }
)

/**
 * Get filters available to current user based on tier (memoized)
 * Launch promo unlocks all filters regardless of tier.
 */
export const selectAvailableFilters = createSelector(
  [selectFilterDefinitions, selectUserTier, selectIsLaunchPromoForFilters],
  (filterDefinitions, userTier, isLaunchPromo) => {
    if (isLaunchPromo) return filterDefinitions
    return filterDefinitions.filter(filter => {
      return checkTierAccess(userTier, filter.minTier)
    })
  }
)

/**
 * Get locked filters for current user (memoized)
 * Launch promo means no filters are locked.
 */
export const selectLockedFilters = createSelector(
  [selectFilterDefinitions, selectUserTier, selectIsLaunchPromoForFilters],
  (filterDefinitions, userTier, isLaunchPromo) => {
    if (isLaunchPromo) return []
    return filterDefinitions.filter(filter => {
      return !checkTierAccess(userTier, filter.minTier)
    })
  }
)

/**
 * Helper function to check if user's tier has access to a filter
 */
function checkTierAccess(userTier: UserTier, requiredTier: UserTier): boolean {
  // Tier hierarchy - must match backend SUBSCRIPTION_TIERS
  const tierHierarchy: Record<UserTier, number> = {
    'STARTER': 0,
    'PRO': 1,
    'ELITE': 2,
    'UNLIMITED': 3
  }

  return (tierHierarchy[userTier] ?? 0) >= (tierHierarchy[requiredTier] ?? 0)
}

// Export types for use in components
export type { CrimeIncident, PointOfInterest, POILocation, AmenityData, AmenitiesDetailed, PostcodeEnrichmentCache }
